import React from 'react';

import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { isSameDay, format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';

import { StyledKeyboardDatePicker } from '../DateInputCN/styles';

type BaseProps = React.ComponentProps<typeof StyledKeyboardDatePicker>;

export type MultiDatePickerProps = Omit<BaseProps, 'value' | 'onChange'> & {
  value: Date[];
  onChange: (dates: Date[]) => void;
};

export function MultiDatePicker({
  value,
  onChange,
  helperText,
  minDate,
  maxDate,
  InputProps,
  ...rest
}: MultiDatePickerProps) {
  const [selectedDates, setSelectedDates] = React.useState<Date[]>(value ?? []);
  const [open, setOpen] = React.useState(false);
  const ignoreNextChangeRef = React.useRef(false);
  const isClosingRef = React.useRef(false);

  React.useEffect(() => {
    setSelectedDates(value ?? []);
  }, [value]);

  React.useEffect(() => {
    if (open) ignoreNextChangeRef.current = true;
  }, [open]);

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    isClosingRef.current = true;
    setOpen(false);
    window.setTimeout(() => {
      isClosingRef.current = false;
    }, 0);
  };

  const toggleDate = (date: MaterialUiPickersDate) => {
    if (!date) return;
    if (isClosingRef.current) return;
    if (ignoreNextChangeRef.current) {
      ignoreNextChangeRef.current = false;
      return;
    }
    if (!open) return;

    const min = minDate ? new Date(minDate as any) : null;
    const max = maxDate ? new Date(maxDate as any) : null;

    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (min) {
      const minOnly = new Date(min.getFullYear(), min.getMonth(), min.getDate());
      if (dateOnly < minOnly) return;
    }
    if (max) {
      const maxOnly = new Date(max.getFullYear(), max.getMonth(), max.getDate());
      if (dateOnly > maxOnly) return;
    }

    const alreadySelected = selectedDates.some(d => isSameDay(d, date));
    const newDates = alreadySelected ? selectedDates.filter(d => !isSameDay(d, date)) : [...selectedDates, date];

    setSelectedDates(newDates);
    onChange(newDates);
  };

  const renderDay = (
    day: MaterialUiPickersDate,
    selectedDate: MaterialUiPickersDate | null,
    isInCurrentMonth: boolean,
    dayComponent: React.ReactElement,
  ) => {
    if (!day || !isInCurrentMonth) {
      return dayComponent;
    }

    const isSelected = selectedDates.some(d => isSameDay(d, day));

    return React.cloneElement(dayComponent, {
      selected: isSelected,
      style: {
        ...(dayComponent.props.style || {}),
        ...(isSelected
          ? {
              backgroundColor: '#24C361',
              color: '#ffffff',
            }
          : {}),
      },
    });
  };

  const defaultHelperText =
    selectedDates.length > 0
      ? selectedDates
          .slice()
          .sort((a, b) => a.getTime() - b.getTime())
          .map(d => format(d, 'dd/MM/yyyy'))
          .join(', ')
      : undefined;

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBR}>
      <StyledKeyboardDatePicker
        {...rest}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        value={null}
        onChange={toggleDate}
        renderDay={renderDay}
        helperText={helperText ?? defaultHelperText}
        minDate={minDate}
        maxDate={maxDate}
        InputProps={{ ...InputProps, onClick: handleOpen }}
        inputProps={{ readOnly: true, style: { cursor: 'pointer' } }}
      />
    </MuiPickersUtilsProvider>
  );
}
